import React, { useState } from 'react';
import { Upload, CheckCircle, AlertCircle, FileText, Download } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface BulkUploadProps {
  onUploadComplete: () => void;
}

interface ProductRow {
  itmref: string;
  name: string;
  unit: string;
}

interface UploadResult {
  success: boolean;
  message: string;
  processed: number;
  updated: number;
  created: number;
  errors: string[];
}

const BulkUpload: React.FC<BulkUploadProps> = ({ onUploadComplete }) => {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<UploadResult | null>(null);
  const [previewData, setPreviewData] = useState<ProductRow[]>([]);
  const [showPreview, setShowPreview] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setResult(null);
      setPreviewData([]);
      setShowPreview(false);
    }
  };

  const parseCSV = (csvText: string): ProductRow[] => {
    const lines = csvText.split('\n');
    const products: ProductRow[] = [];
    
    // Find the header row (contains ITMREF, ITMDES1, STU)
    let headerIndex = -1;
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      if (line.includes('ITMREF') && line.includes('ITMDES1') && line.includes('STU')) {
        headerIndex = i;
        break;
      }
    }
    
    if (headerIndex === -1) {
      throw new Error('Could not find header row with ITMREF, ITMDES1, and STU columns');
    }

    // Parse header to get column positions
    const headerLine = lines[headerIndex];
    const headers = headerLine.split(';').map(h => h.replace(/"/g, '').trim());
    
    const itmrefIndex = headers.indexOf('ITMREF');
    const nameIndex = headers.indexOf('ITMDES1');
    const unitIndex = headers.indexOf('STU');
    
    if (itmrefIndex === -1 || nameIndex === -1 || unitIndex === -1) {
      throw new Error('Required columns not found. Need ITMREF, ITMDES1, and STU');
    }

    // Process data rows (skip header and any rows before it)
    for (let i = headerIndex + 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;
      
      const columns = line.split(';').map(col => col.replace(/"/g, '').trim());
      
      const itmref = columns[itmrefIndex];
      const name = columns[nameIndex];
      const unit = columns[unitIndex];
      
      // Skip rows with missing required data
      if (!itmref || !name || !unit || itmref === 'ITMREF') continue;
      
      products.push({
        itmref,
        name,
        unit
      });
    }
    
    return products;
  };

  const handlePreview = async () => {
    if (!file) return;
    
    try {
      setUploading(true);
      const csvText = await file.text();
      const products = parseCSV(csvText);
      
      // Show first 10 rows for preview
      setPreviewData(products.slice(0, 10));
      setShowPreview(true);
    } catch (error: any) {
      console.error('Error parsing CSV:', error);
      setResult({
        success: false,
        message: `Error parsing CSV: ${error.message}`,
        processed: 0,
        updated: 0,
        created: 0,
        errors: [error.message]
      });
    } finally {
      setUploading(false);
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    
    setUploading(true);
    const errors: string[] = [];
    let processed = 0;
    let updated = 0;
    let created = 0;

    try {
      const csvText = await file.text();
      const products = parseCSV(csvText);
      
      console.log(`Processing ${products.length} products...`);
      
      // Process in batches to avoid overwhelming the database
      const batchSize = 100;
      for (let i = 0; i < products.length; i += batchSize) {
        const batch = products.slice(i, i + batchSize);
        
        for (const product of batch) {
          try {
            processed++;
            
            // Check if product already exists
            const { data: existingProduct, error: searchError } = await supabase
              .from('products')
              .select('id, name, unit')
              .eq('item_ref', product.itmref)
              .maybeSingle();
            
            if (searchError) {
              errors.push(`Error checking product ${product.itmref}: ${searchError.message}`);
              continue;
            }
            
            if (existingProduct) {
              // Update existing product
              const { error: updateError } = await supabase
                .from('products')
                .update({
                  name: product.name,
                  unit: product.unit,
                  updated_at: new Date().toISOString()
                })
                .eq('item_ref', product.itmref);
              
              if (updateError) {
                errors.push(`Error updating product ${product.itmref}: ${updateError.message}`);
              } else {
                updated++;
              }
            } else {
              // Create new product
              const { error: insertError } = await supabase
                .from('products')
                .insert({
                  item_ref: product.itmref,
                  name: product.name,
                  unit: product.unit
                });
              
              if (insertError) {
                errors.push(`Error creating product ${product.itmref}: ${insertError.message}`);
              } else {
                created++;
              }
            }
          } catch (error: any) {
            errors.push(`Unexpected error processing ${product.itmref}: ${error.message}`);
          }
        }
        
        // Small delay between batches
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      setResult({
        success: errors.length === 0,
        message: errors.length === 0 
          ? 'Master data uploaded successfully!' 
          : `Upload completed with ${errors.length} errors`,
        processed,
        updated,
        created,
        errors: errors.slice(0, 10) // Show first 10 errors
      });
      
      if (errors.length === 0) {
        onUploadComplete();
      }
      
    } catch (error: any) {
      console.error('Error processing CSV:', error);
      setResult({
        success: false,
        message: `Error processing CSV: ${error.message}`,
        processed,
        updated,
        created,
        errors: [error.message]
      });
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    const template = `"WMSARSTD";"Product Inventory";"HELLIVE";"08/07/2025";"15:27:36";
"---";"---";"---"
"CONSSAR1";"Product Inventory";
"";""
"Selections";
"FCY";"Site";"MDLS";
"ZFCY";" ";"MDLS";
"ITMREFSTR";"First Product";"";
"ZITMREFSTR";" ";"";
"ITMREFEND";"Last Product";"";
"ZITMREFEND";" ";"";
"";""
"";
"SRTKEY";"Sort order";"Product Number";
"QTYTYP";"Quantity Selection";"Physical qty";
"---";"---";"---"
"WMSARSTD";"Product Inventory";
"";""
"";
"ITMREF";"ITMDES1";"STU";"CFGLIN";"QTYTYP";"TCLCOD";"SEAKEY";"TOTSTO";"PLFSTO";"ALLSTO";"ORDSTO";"TRFSTO";"TRASTO";"BPRSTO";"SCOSTO";"BUY";"DETSHT";"GLOALL";"GLOSHT";"SALSTO";"WAISTO";"TSICOD1";"TSICOD2";"TSICOD3";"TSICOD4";"TSICOD5";
"Product";"Description";"STK";"Product Line";"Quantity Type";"Category";"Search Key";"Total internal stock";"Total stock waiting put-away";"Total allocated";"On Order";"In-transit";"Transferred";"Total loan stock";"Total sub-con stock";"Buyer";"Detail shortage";"Global Allocated";"Global shortage";"On Sales Orders";"Suspended trans.";"Brand";"Medicine";"Groupage";"Storage";"Group 5";
"EXAMPLE-PRODUCT-001";"Example Product 1";"EA";"";"Physical qty";"EXAMPLE";"";"0";"0";"0";"0";"0";"0";"0";"0";"";"0";"0";"0";"0";"0";"00";"99";"85";"10";"";
"EXAMPLE-PRODUCT-002";"Example Product 2";"BOX";"";"Physical qty";"EXAMPLE";"";"0";"0";"0";"0";"0";"0";"0";"0";"";"0";"0";"0";"0";"0";"00";"99";"85";"10";"";`;
    
    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'master_data_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="card bg-base-100 shadow-xl">
      <div className="card-body">
        <h2 className="card-title flex items-center gap-2">
          <Upload className="w-5 h-5" />
          Bulk Upload Master Data
        </h2>
        
        <div className="alert alert-info">
          <FileText className="w-5 h-5" />
          <div>
            <div className="font-semibold">Upload CSV to update product information</div>
            <div className="text-sm">This will update SKU codes and product descriptions. Note: Barcodes need to be added manually.</div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex gap-2">
            <div className="form-control flex-1">
              <input
                type="file"
                accept=".csv"
                className="file-input file-input-bordered w-full"
                onChange={handleFileChange}
                disabled={uploading}
              />
            </div>
            <button
              className="btn btn-outline"
              onClick={downloadTemplate}
              disabled={uploading}
            >
              <Download className="w-4 h-4 mr-2" />
              Template
            </button>
          </div>

          {file && (
            <div className="flex gap-2">
              <button
                className="btn btn-outline"
                onClick={handlePreview}
                disabled={uploading}
              >
                <FileText className="w-4 h-4 mr-2" />
                Preview
              </button>
              <button
                className="btn btn-primary"
                onClick={handleUpload}
                disabled={uploading}
              >
                {uploading ? (
                  <>
                    <span className="loading loading-spinner loading-sm mr-2"></span>
                    Processing...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload
                  </>
                )}
              </button>
            </div>
          )}

          {showPreview && previewData.length > 0 && (
            <div className="space-y-2">
              <h3 className="font-semibold">Preview (first 10 rows):</h3>
              <div className="overflow-x-auto">
                <table className="table table-compact w-full">
                  <thead>
                    <tr>
                      <th>Item Reference</th>
                      <th>Product Name</th>
                      <th>Unit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.map((row, index) => (
                      <tr key={index}>
                        <td className="font-mono text-sm">{row.itmref}</td>
                        <td>{row.name}</td>
                        <td>
                          <span className="badge badge-outline">{row.unit}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {result && (
            <div className={`alert ${result.success ? 'alert-success' : 'alert-error'}`}>
              {result.success ? (
                <CheckCircle className="w-5 h-5" />
              ) : (
                <AlertCircle className="w-5 h-5" />
              )}
              <div>
                <div className="font-semibold">{result.message}</div>
                <div className="text-sm">
                  Processed: {result.processed} | Created: {result.created} | Updated: {result.updated}
                </div>
                {result.errors.length > 0 && (
                  <details className="mt-2">
                    <summary className="cursor-pointer text-sm font-medium">
                      Show errors ({result.errors.length})
                    </summary>
                    <ul className="mt-1 text-sm list-disc list-inside">
                      {result.errors.map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkUpload; 